import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { PageHeader } from "@features/page-header/page-header";
import { PageTools } from "@features/page-tools/page-tools";
import withSearchQueryParamsContext from "@components/params/withSearchQueryParamsContext";
import {
    AUTOCOMPLETE_TABLE_LIST_TYPE,
    DRIVE_FOLDER_SEARCH_TYPE,
    GROUPING_VIEW_TYPE,
    LIST_VIEW_TYPE,
    PROPERTY_PANEL_TYPE,
    TABLE_LIST_TYPE,
    DND_TYPES,
} from "@constants";
import VersionedComponent from "@components/VersionedComponent";
import { getCollections, updateCollectionState } from "../../modules/collections";
import { Trans, useTranslation } from "react-i18next";
import { IconAdd, IconArchive, IconDelete, IconDownload, IconShareLink, ImageEmpty } from "@mzc-cloudplex/icons";
import menuService from "@services/menuService";
import TooltipButton from "src/components_v2/tooltip-button/tooltip-button";
import theme from "@styles/theme";
import ListTools from "src/features/list-tools/list-tools";
import { Pagination } from "@components_v2/pgaination/pagination";
import compareVersions, {
    VERSION_CM_11164_COLLECTION_FOLDER_VIEW,
    VERSION_CM_12197_COLLECTION_SHARE_LINK,
    VERSION_CM_12198_COLLECTION_DOWNLOAD,
    VERSION_CM_12290_COLLECTION_ARCHIVE,
    VERSION_CM_12923_LIST_SEARCHBAR_FILTER,
    VERSION_CM_9766_COLLECTIONS,
} from "../../cores/version";
import { Box, Button, CircularProgress, Divider, Grid, Typography } from "@mzc-pdc/ui";
import ListTableCollections from "./list-table-collections";
import SearchTextField from "@features/list-search/search-textfield";
import DialogCreateCollection from "./dialog-create";
import { closePropertyPanel, togglePropertyPanel } from "@modules/propertyPanel";
import DialogDelete from "src/features/dialog/dialog-delete";
import { deleteCollectionAPI } from "@modules/apis/collection";
import { updateCollection } from "@modules/collection";
import { setNotification } from "@modules/notification";
import ListDragPreview from "@components/Preview/ListDragPreview";
import BreadcrumbsWithRouterPath from "@containers/BreadcrumbsWithRouterPath/BreadcrumbsWithRouterPath";
import * as yup from "yup";
import useFilter from "@hooks/useFilter";
import CardCollection from "@routes/collections/card-collection";
import useSettings from "@hooks/useSettings";
import { loadViewInfo, saveViewInfo } from "@cores/viewStorage";
import TreeViewLayout from "@components/tree/TreeViewLayout";
import { createDriveFolder, getFoldersList } from "@modules/folderTreeView";
import fp from "lodash/fp";
import ReactDOM from "react-dom";
import Loadable from "@react-loadable/revised";
import ListViewSelectButton from "@components_v2/list-view-select-button/list-view-select-button";
import useArchive from "@hooks/useArchive";
import { listJobs } from "@modules/bulkJobs";
import DialogArchive from "@features/dialog/dialog-archive";
import useDownloadJobs from "@hooks/useDownloadJobs";
import ErrorView from "@routes/errors/error-view";
import useCheckPermission from "@hooks/useCheckPermission";
import useFolder from "@hooks/useFolder";
import BreadcrumbV2 from "@components_v2/breadcrumb/breadcrumb-v2";
import useCustomMenus from "@hooks/useCustomMenus";
import { siteMode } from "@cores/siteMode";
import CollapsibleSplitTwoTierGridLeft from "@components_v2/grid/collapsible-split-two-tier-grid-left";

const LoadableFolderRenameModal = Loadable({
    loader: () => import("@components/modals/FolderRenameModal"),
    loading: () => null,
});

const LoadableDownloadLinkModal = Loadable({
    loader: () => import("@components/modals/DownloadLinkModal"),
    loading: () => null,
});

const LoadableNewFolderModal = Loadable({
    loader: () => import("../../components/modals/NewFolderModal"),
    loading: () => null,
});

const ListCollections = ({ searchQueryParams }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const match = useRouteMatch();
    const { t } = useTranslation();
    const { pathname } = useLocation();

    const { spaceId, projectId } = menuService.parse(pathname);
    const { removeFilter } = useFilter(TABLE_LIST_TYPE.ASSET);
    const { isCardView, updateIsCardView, getSettingData, resourceSettings } = useSettings({
        type: "General",
        domain: "collections",
    });
    const { requestArchive } = useArchive();
    const { requestDownloadJob } = useDownloadJobs();
    const COLLECTIONS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Assets" });
    const SHARES_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Shares" });
    const DOWNLOAD_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Download" });
    const FOLDERS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Folders" });
    const { onClickFolderTree, onRenameFolder, currentFolderInfo } = useFolder({
        domain: "COLLECTION",
    });
    const {
        getSettingData: getSettingMenuName,
        settingsPending: settingsPendingMenuName,
        resourceSettings: settingsMenuName,
    } = useSettings({
        type: "MenuName",
        domain: "assets",
    });
    const { getCustomMenuBreadcrumb } = useCustomMenus();
    const isLibrary = siteMode.get() === "CMS";

    const { stage, project, collections, folderTreeView } = useSelector((store) => ({
        stage: store.stage,
        project: store.project,
        collections: store.collections,
        folderTreeView: store.folderTreeView,
    }));
    const [tokenPaginationInfo, setTokenPaginationInfo] = useState({ currentPage: 0, data: [] });
    const [selectedItems, setSelectedItems] = useState([]);
    const [clickedItem, setClickedItem] = useState(null);
    const [isQuickSearch, setIsQuickSearch] = useState(true);
    const [isMountPending, setIsMountPending] = useState(true);
    const [modal, setModal] = useState(false);
    const [actionTarget, setActionTarget] = useState(null);
    const [actionPending, setActionPending] = useState({});
    const [isControlSticky, setIsControlSticky] = useState(false);
    const stickyDomRef = useRef();
    const [offset, setOffset] = useState(0);
    const [selectedItemsDragging, setSelectedItemsDragging] = useState(false);
    const listDragPreviewRef = useRef(null);

    const isCustomMenu = useMemo(() => {
        return settingsMenuName?.settings?.MenuName?.selectMenu?.value === "custom";
    }, [settingsMenuName?.settings?.MenuName?.selectMenu]);

    const handleScroll = (e) => {
        if (!stickyDomRef.current) return;
        const boxSize = stickyDomRef.current.getBoundingClientRect();
        const isSticky = boxSize.top <= window.scrollY;

        setIsControlSticky(isSticky);
    };

    const loadCollections = async (params, page) => {
        const nextToken = await dispatch(getCollections(params));

        if (nextToken) {
            const copiedTokens = [...tokenPaginationInfo.data];
            copiedTokens[page] = nextToken ?? null;

            setTokenPaginationInfo({ currentPage: page, data: copiedTokens });
        }
    };

    const onChangeOffset = async (page) => {
        setOffset(page);
    };

    const onChangeLimit = (limit) => {
        setTokenPaginationInfo((prevState) => ({ currentPage: 1, data: [] }));
        searchQueryParams.updateSearchQueryParams({
            action: "push",
            params: { ...searchQueryParams.params, limit },
        });
        saveCollectionViewInfo({ ...searchQueryParams.params, limit });
    };

    const onClickRow = (id) => {
        setClickedItem(id);
        dispatch(
            togglePropertyPanel({
                targetId: id,
                type: PROPERTY_PANEL_TYPE.COLLECTION,
                projectId: project.id,
            }),
        );
    };

    const onDoubleClickRow = (collectionId) => {
        if (isCustomMenu) {
            return history.push(`${pathname.split("/search")[0]}/${collectionId}`);
        }

        return isLibrary
            ? history.push(menuService.paths.library.assetsCollectionDetail(spaceId, projectId, collectionId))
            : history.push(menuService.paths.assetsCollectionDetail(spaceId, projectId, collectionId));
    };

    const onClickRefresh = () => {
        loadCollections(getParsedCollectionsParams, offset);
    };

    const onToggleActiveView = (value) => {
        dispatch(updateCollectionState({ activeView: value.isActiveView, hierarchyType: value.hierarchyType }));
        saveCollectionViewInfo({
            stageId: spaceId,
            projectId: projectId,
            activeView: value.isActiveView,
            hierarchyType: value.hierarchyType,
            virtualFolderId: value.virtualFolderId,
            virtualDriveId: value.virtualDriveId,
            isCardView: isCardView,
        });
    };

    const onClickViewType = (value) => {
        if (value === isCardView) return;
        updateIsCardView(value);
    };

    const getParsedCollectionsParams = useMemo(() => {
        const { hasItems, ids, limit, name, tags, createdAt, creator, updatedAt, updater, virtualFolderId } =
            searchQueryParams.params;

        const params = {
            nextToken: offset > 0 ? tokenPaginationInfo.data[offset - 1] : undefined,
            limit,
            createdAt: createdAt ? `${createdAt.start}~${createdAt.end}` : undefined,
            createdById: creator,
            hasItems,
            ids,
            name,
            tags: tags,
            updatedAt: updatedAt ? `${updatedAt.start}~${updatedAt.end}` : undefined,
            updatedById: updater,
        };

        if (compareVersions(stage.version, VERSION_CM_11164_COLLECTION_FOLDER_VIEW) >= 0) {
            params.virtualFolderId =
                collections.activeView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY ? virtualFolderId : undefined;
        }

        return params;
    }, [searchQueryParams.params, offset, collections.activeView, stage]);

    const canEditCollections = COLLECTIONS_PERMISSIONS.EDIT;
    const isDraggableByHierarchy = useMemo(
        () => searchQueryParams.params?.activeView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY && canEditCollections,
        [searchQueryParams.params?.activeView, canEditCollections],
    );

    const onDragStartItem = useCallback(
        (e) => {
            if (selectedItems?.length && listDragPreviewRef.current) {
                e.dataTransfer.setDragImage(listDragPreviewRef.current, 0, 0);
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData(DND_TYPES.LIST_ITEM, "");
                setSelectedItemsDragging(true);
            }
        },
        [selectedItems],
    );

    const onDragEndItem = useCallback(() => {
        setSelectedItemsDragging(false);
    }, []);

    const onDropTreeItem = useCallback(
        async (targetFolder) => {
            if (!targetFolder?.id) return;

            // 드래그 소스는 항상 selectedItems. actionTarget(컨텍스트 메뉴 경로)은 DnD와 분리.
            const targets = selectedItems;
            if (!targets?.length) return;

            // 현재 폴더로 드롭한 경우 무시
            if (targetFolder.id === searchQueryParams.params.virtualFolderId) return;

            const fromFolderId = searchQueryParams.params.virtualFolderId;
            const fromFolderName = folderTreeView.folderList?.data?.[fromFolderId]?.name ?? "";
            const toFolderName = targetFolder.name ?? "";

            try {
                const results = await Promise.allSettled(
                    targets.map((item) =>
                        dispatch(
                            updateCollection({
                                id: item.id,
                                params: { virtualFolder: { id: targetFolder.id } },
                                ignoreAction: true, // 목록 화면에서는 상세(collection.data) 상태를 건드리지 않음
                            }),
                        ),
                    ),
                );

                const rejected = results.filter((r) => r.status === "rejected");
                const fulfilled = results.filter((r) => r.status === "fulfilled");

                if (!fulfilled.length) {
                    throw rejected[0]?.reason ?? new Error("All updates rejected");
                }

                dispatch(
                    setNotification({
                        type: rejected.length > 0 ? "warning" : "success",
                        contents: t(
                            "common::msg::Move completed from {{item1}} to {{item2}}.",
                            "Move completed from {{item1}} to {{item2}}.",
                            { item1: fromFolderName, item2: toFolderName },
                        ),
                    }),
                );

                await loadCollections(getParsedCollectionsParams, offset);
                setSelectedItems([]);
            } catch (error) {
                console.error("Failed to move collections on drop", error);
                const errorMsg = error?.response?.data?.message;
                const baseMsg = t(
                    "common::msg::Error moving from {{item1}} to {{item2}}.",
                    "Error moving from {{item1}} to {{item2}}.",
                    { item1: fromFolderName, item2: toFolderName },
                );
                dispatch(
                    setNotification({
                        type: "error",
                        contents: errorMsg ? `${baseMsg} - ${errorMsg}` : baseMsg,
                    }),
                );
            } finally {
                setSelectedItemsDragging(false);
            }
        },
        [
            selectedItems,
            searchQueryParams.params.virtualFolderId,
            folderTreeView.folderList?.data,
            getParsedCollectionsParams,
            offset,
            dispatch,
            t,
        ],
    );

    const onHandleClickSubmitSearch = useCallback(
        (values) => {
            let url = isLibrary
                ? menuService.paths.library.assetsCollectionsSearch(spaceId, projectId)
                : menuService.paths.assetsCollectionsSearch(spaceId, projectId);
            if (isCustomMenu) {
                url = `${pathname}/search`;
            }

            searchQueryParams.resetSearchQueryParams({
                url,
                action: "push",
                params: {
                    ...values,
                    // keyword: isQuickSearch ? values.keyword : values.name,
                    // name: isQuickSearch ? values.keyword : values.name,
                },
            });
        },
        [searchQueryParams, isLibrary, isCustomMenu],
    );

    const onChangeNumSelected = (checkAll) => {
        if (checkAll) {
            setSelectedItems([].concat(collections.data));
        } else {
            setSelectedItems([]);
        }
    };

    const onHandleChangeSelectItem = (collection) => {
        if (selectedItems.find((item) => item.id === collection.id)) {
            setSelectedItems(selectedItems.filter((v) => v.id !== collection.id));
        } else {
            setSelectedItems([...selectedItems, collection]);
        }
    };

    const onToggleModal = (key, value, actionTarget = null) => {
        if (value) {
            setModal(key);
            setActionTarget(actionTarget ? [actionTarget] : null);
        } else {
            setModal("");
            setActionTarget(null);
            setSelectedItems([]);
        }
    };

    const onHandleClickDownloadCollection = useCallback(
        (collection) => {
            let excutedByCheckbox, downloadRequestTargets;

            excutedByCheckbox = !collection ? true : false;

            downloadRequestTargets = {
                resourceIds: excutedByCheckbox ? selectedItems.map((item) => item.id) : [collection.id],
                projectId: projectId,
                resourceName: excutedByCheckbox ? selectedItems[0].name : collection?.name,
            };

            requestDownloadJob(
                "COLLECTION",
                downloadRequestTargets,
                excutedByCheckbox ? selectedItems.length : 1,
                false,
            );
        },
        [selectedItems, requestDownloadJob],
    );

    const onHandleClickDownloadFolder = useCallback((folder) => {
        const downloadRequestTargets = {
            virtualFolder: { id: folder.id, recursive: false },
            projectId: projectId,
            resourceName: folder.name,
        };
        requestDownloadJob("COLLECTION", downloadRequestTargets, 1, false);
    }, []);

    const getListToolsButtons = useCallback(
        (isResponsive) => {
            if (isLibrary) return [];

            return [
                <VersionedComponent key="share-link" version={VERSION_CM_12197_COLLECTION_SHARE_LINK}>
                    <TooltipButton
                        circled
                        title={t("common::label::Share link", "Share link")}
                        size={"small"}
                        onClickButton={() => {
                            onToggleModal("shareLink", true);
                        }}
                        buttonIcon={<IconShareLink size={16} />}
                        buttonText={t("common::label::Share link", "Share link")}
                        isDivided={true}
                        isResponsive={isResponsive}
                        disabled={!SHARES_PERMISSIONS.EXTERNAL}
                    />
                </VersionedComponent>,
                <VersionedComponent key="download" version={VERSION_CM_12198_COLLECTION_DOWNLOAD}>
                    <TooltipButton
                        circled
                        title={t("common::label::Download", "Download")}
                        size={"small"}
                        onClickButton={() => {
                            onHandleClickDownloadCollection();
                        }}
                        buttonIcon={<IconDownload size={16} />}
                        buttonText={t("common::label::Download", "Download")}
                        isDivided={false}
                        isResponsive={isResponsive}
                        disabled={!DOWNLOAD_PERMISSIONS.DOWNLOAD}
                    />
                </VersionedComponent>,
                <VersionedComponent key="archive" version={VERSION_CM_12290_COLLECTION_ARCHIVE}>
                    <TooltipButton
                        circled
                        title={t("common::label::Archive", "Archive")}
                        size={"small"}
                        onClickButton={() => {
                            onToggleModal("archive", true);
                        }}
                        buttonIcon={<IconArchive size={16} />}
                        buttonText={t("common::label::Archive", "Archive")}
                        isDivided={true}
                        isResponsive={isResponsive}
                        disabled={!COLLECTIONS_PERMISSIONS.ARCHIVE}
                    />
                </VersionedComponent>,
                <TooltipButton
                    key="delete"
                    circled
                    title={t("common::label::Delete", "Delete")}
                    size={"small"}
                    onClickButton={() => onToggleModal("delete", true)}
                    buttonIcon={<IconDelete size={16} />}
                    buttonText={t("common::label::Delete", "Delete")}
                    isResponsive={isResponsive}
                    disabled={!COLLECTIONS_PERMISSIONS.EDIT}
                />,
            ];
        },
        [selectedItems, isLibrary],
    );

    const pagination = useMemo(() => {
        return (
            <Pagination
                page={offset}
                limit={getParsedCollectionsParams?.limit}
                hasNextToken={tokenPaginationInfo.data[offset]}
                pageCount={collections.data?.length}
                onChangePage={onChangeOffset}
                onChangeLimit={onChangeLimit}
                onClickRefresh={onClickRefresh}
            />
        );
    }, [collections, tokenPaginationInfo, offset]);

    const onHandleClickCreate = () => {
        onToggleModal("create", true);
    };

    const onHandleCLickResetParams = () => {
        searchQueryParams.resetSearchQueryParams({
            action: "replace",
            params: {},
        });
    };

    const onHandleDeleteCollections = async () => {
        try {
            setActionPending({ ...actionPending, delete: true });

            const items = actionTarget ? actionTarget : selectedItems;

            const results = await Promise.allSettled(
                items.map(async (item) => deleteCollectionAPI(stage.endpoint, project.id, item.id)),
            );

            const rejectedResults = results.filter((result) => result.status === "rejected");
            const fulfilledResults = results.filter((result) => result.status === "fulfilled");
            if (!fulfilledResults.length) {
                throw new Error("rejected");
            } else {
                //성공 메세지
                dispatch(
                    setNotification({
                        type: "success",
                        contents: (
                            <Trans
                                i18nKey={`common::msg::${
                                    rejectedResults.length > 0 ? `Partial deletion` : `Deletion`
                                } of collection {{item}}${
                                    selectedItems.length > 1 ? ` and {{count}} others have` : " has"
                                } been completed.`}
                                values={{ item: items[0].name, count: items.length - 1 }}
                            />
                        ),
                    }),
                );
            }

            loadCollections(getParsedCollectionsParams);
            onToggleModal();
        } catch (error) {
            console.error(error);
            setNotification({
                type: "error",
                contents: t(
                    `common::msg::There was an error deleting collection${selectedItems.length > 1 ? "s" : ""}`,
                ),
            });
        } finally {
            setActionPending({ ...actionPending, delete: undefined });
        }
    };

    const onHandleClickArchiveCollection = async (isFolder, option) => {
        try {
            setActionPending({ ...actionPending, archive: true });

            let targets = {};
            if (isFolder) {
                targets = {
                    virtualFolder: {
                        id: folderTreeView.folderList.selectedFolder.folder.id,
                        recursive: false, //TODO: 하위폴더 포함 여부 추가
                    },
                };
            } else {
                const ids = selectedItems?.length ? selectedItems.map((i) => i.id) : [actionTarget[0].id];
                targets = { ids };
            }
            const data = {
                archiveClass: option,
                targets: {
                    collection: targets,
                },
            };
            const isMulti = selectedItems?.length > 1 ? true : false;

            await requestArchive(isMulti, data, async () => {
                dispatch(listJobs());
                await onClickRefresh();
                setSelectedItems([]);
            });
        } catch (e) {
        } finally {
            setActionPending({ ...actionPending, archive: false });
            onToggleModal();
        }
    };

    const onHandleClickFolderTree = (item, isClickArrow, checkUseHistory = false, options) => {
        onClickFolderTree(item, isClickArrow, checkUseHistory, onClickTreeItem, options);
    };

    useEffect(() => {
        if (isMountPending || !getParsedCollectionsParams.limit || !COLLECTIONS_PERMISSIONS.READ) return;
        if (collections?.activeView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY) {
            if (!getParsedCollectionsParams.virtualFolderId) {
                return;
            }
        }

        loadCollections(getParsedCollectionsParams, offset);
        //검색
    }, [isMountPending, getParsedCollectionsParams]);

    useEffect(() => {
        if (history.location.search === "") searchQueryParams.resetSearchQueryParams({ action: "push" });

        dispatch(updateCollectionState({ pending: true }));
        const { projectId: _, ...viewInfo } = loadViewInfo("collectionsViewInfo", spaceId, projectId) ?? {};

        if (viewInfo?.virtualFolderId && !viewInfo?.virtualDriveId) {
            delete viewInfo.virtualFolderId;
        }

        const inputParams = { ...searchQueryParams.params };

        let newParams = { ...viewInfo, ...inputParams };
        if (!newParams.activeView) {
            newParams = {
                ...newParams,
                activeView:
                    compareVersions(stage.version, VERSION_CM_11164_COLLECTION_FOLDER_VIEW) >= 0 ? "hierarchy" : "list",
                hierarchyType: GROUPING_VIEW_TYPE.FOLDER,
            };
        }
        if (compareVersions(stage.version, VERSION_CM_11164_COLLECTION_FOLDER_VIEW) < 0) {
            delete newParams.virtualFolderId;
            delete newParams.virtualDriveId;
            newParams.activeView = "list";
        }

        dispatch(updateCollectionState({ activeView: newParams.activeView ?? "list" }));

        getSettingData("Asset");
        getSettingMenuName("MenuName");

        searchQueryParams.resetSearchQueryParams({
            action: "replace",
            params: newParams,
        });

        window.addEventListener("scroll", handleScroll);
        if (isMountPending) setIsMountPending(false);
        return () => {
            setOffset(0);
            window.removeEventListener("scroll", handleScroll);
            dispatch(closePropertyPanel());
            //dispatch(resetCollections());
        };
    }, []);

    const onHandleCloseCreateCollection = () => {
        onToggleModal("");
        removeFilter(AUTOCOMPLETE_TABLE_LIST_TYPE.AUTOCOMPLETE_TABLE_ASSET);
    };

    const saveCollectionViewInfo = (queryParams) => {
        //기존 방식은 함수형으로 바꾸면서 beforeUnmount->cleanUp으로 변경하니 queryParameter가 초기화 되고나서의 값이 들어와서 항상 이상한 값으로 들어가있음.
        //Note: 다음에 넘어갈 파라미터를 가지고 있어야되는데 onClickTreeItem 직후 호출하니까 url이 안바뀐 상태임.
        //문제는 해결되었는데 다른 탭에 갔다가 에셋으로 돌아오면 잠깐 localStorage가 아무값도 없다가 해당탭으로 변경됨
        saveViewInfo("collectionsViewInfo", {
            stageId: spaceId,
            projectId: projectId,
            activeView: queryParams.activeView,
            hierarchyType: queryParams.hierarchyType,
            virtualFolderId: queryParams.virtualFolderId,
            virtualDriveId: queryParams.virtualDriveId,
            isCardView: isCardView,
            limit: queryParams.limit,
        });
    };

    const onClickTreeItem = (item, isList) => {
        setSelectedItems([]);
        setTokenPaginationInfo({ currentPage: 0, data: [] });

        let params = {
            limit: searchQueryParams.params.limit,
            virtualFolderId: item.hierarchyType === GROUPING_VIEW_TYPE.FOLDER ? item.id : "",
            virtualDriveId: item.hierarchyType === GROUPING_VIEW_TYPE.FOLDER ? item.driveId : "",
            hierarchyType: item.hierarchyType,
            activeView: LIST_VIEW_TYPE.HIERARCHY,
        };

        saveCollectionViewInfo(params);

        if (params.activeView.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY && !isList) {
            const isInitialRender = !searchQueryParams.params.virtualFolderId;

            searchQueryParams.resetSearchQueryParams({
                action: isInitialRender ? "replace" : "push",
                params,
            });
        }
    };

    const onHandleFolderView = (item) => {
        const composer = fp.pipe(
            fp.reduce((acc, current) => {
                const folderInfo = folderTreeView.folderList.data[current];
                acc.push(folderInfo);
                return acc;
            }, []),
        );
        const folders = composer(item.children || []);

        // setIsSelectedFolder(item);
        // setFolders(folders);
    };

    const afterMutationWorks = (action) => {
        setSelectedItems([]);
        setActionTarget(null);
        if (action)
            dispatch(
                setNotification({
                    type: "success",
                    contents: t(`common::msg::${"{{action}} completed."}`, {
                        action: t(`common::label::${action}`, action),
                    }),
                }),
            );
    };

    const collectionBreadcrumbMenu = useMemo(() => {
        if (isLibrary) {
            return [];
        }

        return [
            {
                label: "New Folder",
                onClick: () => onToggleModal("createFolder", true),
                clickClose: true,
                buttonDisabled: !FOLDERS_PERMISSIONS.EDIT,
            },
            {
                label: "Share link",
                onClick: () => onToggleModal("shareLinkFolder", true),
                clickClose: true,
                divider: true,
                disabled: compareVersions(stage.version, VERSION_CM_12197_COLLECTION_SHARE_LINK) < 0,
                buttonDisabled: !SHARES_PERMISSIONS.EXTERNAL,
            },
            {
                label: "Rename",
                onClick: () => onToggleModal("renameFolder", true),
                clickClose: true,
                disabled: folderTreeView.folderList.selectedFolder.folder?.isRoot,
                buttonDisabled: !FOLDERS_PERMISSIONS.EDIT,
                divider: true,
            },
            {
                label: "Download",
                onClick: () => onHandleClickDownloadFolder(folderTreeView.folderList.selectedFolder.folder),
                clickClose: true,
                disabled: compareVersions(stage.version, VERSION_CM_12198_COLLECTION_DOWNLOAD) < 0,
                buttonDisabled: !DOWNLOAD_PERMISSIONS.DOWNLOAD,
                divider: true,
            },
            {
                label: "Archive",
                onClick: () => onToggleModal("archiveFolder", true),
                clickClose: true,
                disabled: compareVersions(stage.version, VERSION_CM_12290_COLLECTION_ARCHIVE) < 0,
                buttonDisabled: !COLLECTIONS_PERMISSIONS.ARCHIVE,
                divider: true,
            },
        ];
    }, [folderTreeView.folderList.selectedFolder.folder, collections, stage, isLibrary]);

    const onClickCreateNewFolder = async (name, folder) => {
        try {
            const params = { name, parent: { id: folder.id } };
            const driveId = folder.driveId;

            await dispatch(createDriveFolder(driveId, params));
            const queryParameters = {
                parentId: folder.id,
                searchType: DRIVE_FOLDER_SEARCH_TYPE.DIRECT_CHILDREN,
            };
            const headerParams = { projectId: projectId };
            await dispatch(
                getFoldersList({
                    driveId,
                    queryParameters,
                    headerParams,
                }),
            );
        } catch (error) {
            return Promise.reject(error);
        }
    };

    const isThumbnailCropped = useMemo(() => {
        return resourceSettings.settings?.Asset?.thumbnailCropOption?.value === "true";
    }, [resourceSettings]);

    if (!COLLECTIONS_PERMISSIONS.READ) {
        return <ErrorView status={403} />;
    }

    if (collections.error && !collections.pending) {
        return <ErrorView status={collections.error.status} />;
    }

    return (
        <VersionedComponent version={VERSION_CM_9766_COLLECTIONS}>
            {modal === "create" && (
                <DialogCreateCollection
                    onClose={onHandleCloseCreateCollection}
                    open={modal === "create"}
                    initialFolder={currentFolderInfo}
                    afterMutation={(id) => {
                        history.push(`${menuService.paths.assetsCollectionDetail(spaceId, projectId, id)}`);
                    }}
                />
            )}
            {/* 새폴더 */}
            {modal === "createFolder" &&
                ReactDOM.createPortal(
                    <LoadableNewFolderModal
                        folder={folderTreeView.folderList.selectedFolder.folder}
                        onClickClose={() => {
                            onToggleModal("createFolder");
                        }}
                        onClickSubmit={onClickCreateNewFolder}
                    />,
                    document.body,
                )}

            <>
                <PageHeader
                    title={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: "25px" }}>
                            {!isMountPending && !settingsPendingMenuName?.settingData && (
                                <>
                                    {isCustomMenu ? (
                                        <BreadcrumbV2 breadcrumb={getCustomMenuBreadcrumb(false)} />
                                    ) : (
                                        <Typography variant={"h3"} color={theme.palette.greySecondary.main}>
                                            {t(`common::label::${("Collections", "Collections")}`)}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Box>
                    }
                    breadcrumb={
                        compareVersions(stage.version, VERSION_CM_12923_LIST_SEARCHBAR_FILTER) < 0 && (
                            <BreadcrumbsWithRouterPath />
                        )
                    }
                />
                <PageTools
                    search={
                        <SearchTextField
                            type={TABLE_LIST_TYPE.COLLECTION}
                            isSearch={false}
                            usePopover={false}
                            placeholder={t(
                                "common::msg::Please search by collection name.",
                                "Please search by collection name.",
                            )}
                            searchQueryParams={searchQueryParams}
                            onClickSubmit={onHandleClickSubmitSearch}
                            onClickReset={onHandleCLickResetParams}
                        />
                    }
                    actions={
                        !isLibrary && (
                            <Button
                                size="medium"
                                startIcon={<IconAdd size={20} />}
                                variant="contained"
                                onClick={onHandleClickCreate}
                                disabled={!COLLECTIONS_PERMISSIONS.EDIT}
                            >
                                <Typography variant="body1_700">{t(`common::label::Create`, `Create`)}</Typography>
                            </Button>
                        )
                    }
                />
                <Divider flexItem />
                <CollapsibleSplitTwoTierGridLeft
                    gridLeftProps={{ minSize: 336, maxSize: 740, defaultSize: 336 }}
                    gridLeft={
                        !isMountPending &&
                        searchQueryParams.params?.activeView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY && (
                            <Grid item flex={0} sx={{ height: "100%" }}>
                                <TreeViewLayout
                                    driveDomain={"Collection"}
                                    resourceType={"Collection"}
                                    searchQueryParams={searchQueryParams}
                                    onDropTreeItem={onDropTreeItem}
                                    onClickTreeItem={onClickTreeItem}
                                    onClickForderTree={onHandleClickFolderTree}
                                    onClickCreateNewFolder={onClickCreateNewFolder}
                                    updateActiveView={onToggleActiveView}
                                    onRefresh={onClickRefresh}
                                />
                            </Grid>
                        )
                    }
                    gridRight={
                        <Grid item xs overflow={"hidden"}>
                            <Grid
                                container
                                height={1}
                                sx={{ overflow: `hidden` }}
                                flexDirection={"column"}
                                flexWrap={"nowrap"}
                            >
                                <ListTools
                                    domain={TABLE_LIST_TYPE.COLLECTION}
                                    isCardView={isCardView}
                                    getButtons={getListToolsButtons}
                                    pageCount={collections.data?.length}
                                    numSelected={selectedItems.length}
                                    page={offset}
                                    limit={searchQueryParams.params.limit}
                                    hasNextToken={tokenPaginationInfo.data[offset]}
                                    onToggleViewType={onClickViewType}
                                    onClickRefresh={onClickRefresh}
                                    onChangeNumSelected={onChangeNumSelected}
                                    onChangePage={onChangeOffset}
                                    onChangeLimit={onChangeLimit}
                                    pagination={pagination}
                                    treeViewProps={{
                                        activeView: collections.activeView,
                                        hierarchyType: GROUPING_VIEW_TYPE.FOLDER,
                                        viewSelectButton:
                                            compareVersions(stage.version, VERSION_CM_11164_COLLECTION_FOLDER_VIEW) >=
                                            0 ? (
                                                <ListViewSelectButton
                                                    domain={"Collection"}
                                                    isActiveView={collections.activeView}
                                                    updateActiveView={onToggleActiveView}
                                                />
                                            ) : null,
                                        breadcrumbMenu: collectionBreadcrumbMenu,
                                        onClickFolderTree: onHandleClickFolderTree,
                                        onToggleModal,
                                    }}
                                />
                                {collections.pending || isMountPending ? (
                                    <Grid item xs>
                                        <Grid container alignItems={"center"} justifyContent={"center"} height={`100%`}>
                                            <CircularProgress color="grey" />
                                        </Grid>
                                    </Grid>
                                ) : (
                                    <React.Fragment>
                                        {selectedItems?.length > 0 && (
                                            <ListDragPreview
                                                ref={listDragPreviewRef}
                                                count={selectedItems.length}
                                                selectedItem={selectedItems[0]}
                                            />
                                        )}
                                        {collections.data?.length > 0 ? (
                                            <>
                                                {isCardView ? (
                                                    <Grid
                                                        container
                                                        spacing={2.5}
                                                        mt={0}
                                                        sx={{ maxHeight: `100%`, overflow: `auto` }}
                                                    >
                                                        {collections?.data?.map((collection) => {
                                                            const isItemSelected = !!selectedItems.find(
                                                                (item) => item.id === collection.id,
                                                            );
                                                            return (
                                                                <CardCollection
                                                                    key={collection.id}
                                                                    collection={collection}
                                                                    checked={isItemSelected}
                                                                    thumbnailCrop={isThumbnailCropped}
                                                                    hiddenCheckbox={
                                                                        getListToolsButtons(false)?.length === 0
                                                                    }
                                                                    isCustomMenu={isCustomMenu}
                                                                    draggable={isDraggableByHierarchy && isItemSelected}
                                                                    onChangeCheck={onHandleChangeSelectItem}
                                                                    onClickRow={onClickRow}
                                                                    onDoubleClickRow={onDoubleClickRow}
                                                                    onClickDownload={onHandleClickDownloadCollection}
                                                                    onToggleModal={onToggleModal}
                                                                    onDragStartItem={onDragStartItem}
                                                                    onDragEndItem={onDragEndItem}
                                                                />
                                                            );
                                                        })}
                                                    </Grid>
                                                ) : (
                                                    <ListTableCollections
                                                        selectedItems={selectedItems}
                                                        clickedItem={clickedItem}
                                                        thumbnailCrop={
                                                            resourceSettings.settings?.Asset?.thumbnailCropOption
                                                                ?.value === "true"
                                                        }
                                                        hiddenCheckbox={getListToolsButtons(false)?.length === 0}
                                                        isCustomMenu={isCustomMenu}
                                                        draggable={isDraggableByHierarchy && selectedItems?.length > 0}
                                                        selectedItemsDragging={selectedItemsDragging}
                                                        onClickRow={onClickRow}
                                                        onDoubleClickRow={onDoubleClickRow}
                                                        onChangeCheck={onHandleChangeSelectItem}
                                                        onToggleModal={onToggleModal}
                                                        onHandleChangeSelectItem={onHandleChangeSelectItem}
                                                        onClickDownload={onHandleClickDownloadCollection}
                                                        onDragStartItem={onDragStartItem}
                                                        onDragEndItem={onDragEndItem}
                                                    />
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <Divider
                                                    orientation={"horizontal"}
                                                    className={"flat"}
                                                    sx={{
                                                        width: `100%`,
                                                        borderColor: `${theme.palette.secondary[100]}`,
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        display: `flex`,
                                                        alignItems: `center`,
                                                        justifyContent: `center`,
                                                        width: `100%`,
                                                        flex: `1`,
                                                        paddingTop: "5%",
                                                        paddingBottom: "5%",
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: `flex`,
                                                            flexDirection: `column`,
                                                            alignItems: `center`,
                                                            justifyContent: `center`,
                                                            width: `400px`,
                                                            height: `400px`,
                                                            textAlign: `center`,
                                                        }}
                                                    >
                                                        <ImageEmpty width={76} height={70} />
                                                        <Typography
                                                            variant={`subtitle1_700`}
                                                            component={"p"}
                                                            gutterBottom
                                                            sx={{ marginTop: `20px` }}
                                                            color={theme.palette.greySecondary.main}
                                                        >
                                                            {t(
                                                                `common::msg::No collection items yet.`,
                                                                `No collection items yet.`,
                                                            )}
                                                        </Typography>
                                                        <Typography
                                                            variant={`body3_700`}
                                                            component={"p"}
                                                            sx={{
                                                                mt: 1,
                                                                color: theme.palette.secondary[600],
                                                            }}
                                                        >
                                                            {t(
                                                                `common::msg::Add it using the “Create” button.`,
                                                                `Add it using the “Create” button.`,
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </>
                                        )}
                                    </React.Fragment>
                                )}
                            </Grid>
                            {modal === "renameFolder" &&
                                ReactDOM.createPortal(
                                    <LoadableFolderRenameModal
                                        folder={folderTreeView.folderList.selectedFolder.folder}
                                        onClickClose={() => onToggleModal()}
                                        onClickSubmit={onRenameFolder}
                                        afterMutationWorks={afterMutationWorks}
                                    />,
                                    document.body,
                                )}
                            {modal === "delete" && (
                                <DialogDelete
                                    items={actionTarget ?? selectedItems}
                                    contents={
                                        <Grid maxWidth={"432px"}>
                                            <Typography variant={"body1_400"} color={theme.palette.secondary[800]}>
                                                <Grid item>
                                                    <span>
                                                        <Trans
                                                            i18nKey={`common::msg::"{{name}}"${
                                                                selectedItems.length > 1 ? ` and {{count}} others` : ""
                                                            } will be deleted forever and you won't be abled to restore it.`}
                                                            values={{
                                                                name: selectedItems[0]?.name ?? actionTarget[0].name,
                                                                count: selectedItems.length - 1,
                                                            }}
                                                        />
                                                    </span>
                                                </Grid>
                                                <Grid item>
                                                    <span>
                                                        {t(
                                                            `common::msg::However, assets included in the collection are not deleted.`,
                                                        )}
                                                    </span>
                                                </Grid>
                                            </Typography>
                                        </Grid>
                                    }
                                    actionPending={actionPending.delete}
                                    onClickClose={() => onToggleModal()}
                                    onClickSubmit={() => {
                                        onHandleDeleteCollections();
                                    }}
                                />
                            )}
                        </Grid>
                    }
                />
                {modal === "archive" && (
                    <DialogArchive
                        active={true}
                        onClose={() => onToggleModal()}
                        onSubmit={onHandleClickArchiveCollection}
                        actionPending={actionPending.archive}
                        data={actionTarget ?? selectedItems}
                    />
                )}
                {modal === "archiveFolder" && (
                    <DialogArchive
                        active={true}
                        onClose={() => onToggleModal()}
                        onSubmit={onHandleClickArchiveCollection}
                        actionPending={actionPending.archive}
                        folder={folderTreeView.folderList.selectedFolder.folder}
                    />
                )}
                {modal === "shareLink" &&
                    ReactDOM.createPortal(
                        <LoadableDownloadLinkModal
                            domain={"COLLECTION"}
                            selectedItems={actionTarget ? actionTarget : selectedItems}
                            onClickClose={() => onToggleModal()}
                            onSubmit={() => {
                                onToggleModal();
                                // afterMutationWorks();
                            }}
                        />,
                        document.body,
                    )}
                {modal === "shareLinkFolder" &&
                    ReactDOM.createPortal(
                        <LoadableDownloadLinkModal
                            domain={"COLLECTION"}
                            selectedFolder={folderTreeView.folderList.selectedFolder.folder}
                            onClickClose={() => onToggleModal()}
                            onSubmit={() => {
                                onToggleModal();
                                // afterMutationWorks();
                            }}
                        />,
                        document.body,
                    )}
            </>
        </VersionedComponent>
    );
};

export default withSearchQueryParamsContext({
    addedSchema: {
        createdEndAt: yup.string(),
        createdStartAt: yup.string(),
        updater: yup.string(),
    },
    viewInfoKey: "collectionsViewInfo",
    setProps: true,
    // removeExtraParams: true,
})(ListCollections);
